/**
 * Shared utility for fetching document metadata for SEO and OG images.
 * Used by both generateMetadata and opengraph-image.tsx
 */

import { cache } from 'react';

import { HOST, SITE_URL } from '@/lib/config';
import type { DocumentPageInterface } from '@/types/pdfTypes';

export interface DocumentMetadataInfo {
  documentTitle: string;
  description: string;
  documentType: 'legislation' | 'nsud' | 'decision' | 'unknown';
  // Legislation-specific fields
  regulationNumber?: string | undefined;
  legislationName?: string | undefined;
  legislationYear?: number | undefined;
  iri?: string | undefined;
  // Decision-specific fields
  caseFileNumber?: string | undefined;
  decisionForm?: string | undefined;
  decisionType?: string | undefined;
  courts?: string[] | undefined;
  // NSUD-specific fields
  nsudCaseNumber?: string | undefined;
  nsudCollegium?: string | undefined;
  nsudDate?: string | undefined;
  // Common fields
  originalSource?: string | undefined;
}

/**
 * Why a metadata fetch produced no document.
 *
 * The distinction is load-bearing for SEO, not cosmetic: `notFound` means the
 * document genuinely does not exist and the page must answer 404, while
 * `unavailable` means the backend errored or timed out on a document that may
 * well exist. Collapsing the two (the pre-#8547 behaviour, a bare `null`) made
 * every transient backend blip render an indexable 200 with fallback metadata —
 * see `documentMetadataOutcome`'s callers for the consequences.
 */
// Not exported: consumers narrow on the returned value's `status` field rather
// than naming the union, and knip fails the build on an unused public export.
type DocumentMetadataOutcome =
  | { status: 'ok'; data: DocumentMetadataInfo }
  | { status: 'notFound' }
  | { status: 'unavailable' };

/**
 * The raw `GET /document/<identifier>` payload, with the same three-way
 * failure split as {@link DocumentMetadataOutcome}.
 */
type DocumentPayloadOutcome =
  | { status: 'ok'; doc: DocumentPageInterface }
  | { status: 'notFound' }
  | { status: 'unavailable' };

/** How a `/document` fetch may be cached — `cache` or `next`, never both. */
type DocumentFetchCachePolicy = Pick<RequestInit, 'cache' | 'next'>;

/**
 * Without a deadline a stalled backend hangs the whole page render — the
 * request holds a Next server worker until the platform kills it, and the
 * crawler sees a timeout rather than a page. Same budget as the corpus
 * sitemap's meta fetch (corpus-sitemap-services.ts), and the abort lands in the
 * catch below as `unavailable`, never `notFound`.
 */
const DOCUMENT_FETCH_TIMEOUT_MS = 8_000;

async function fetchDocumentPayload(
  identifier: string,
  cachePolicy: DocumentFetchCachePolicy
): Promise<DocumentPayloadOutcome> {
  try {
    const response = await fetch(`${HOST}/document/${identifier}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      ...cachePolicy,
      // Minted per call, never module-level: a shared signal would abort every
      // later request once the first deadline passed.
      signal: AbortSignal.timeout(DOCUMENT_FETCH_TIMEOUT_MS),
    });

    if (!response.ok) {
      console.error(
        `Failed to fetch document metadata: ${response.status} ${response.statusText}`
      );
      // Definitively-not-a-document: 404/410 are authoritative, and 400 is the
      // backend's `parse_document_identifier` rejecting the identifier's shape
      // (api/document.py) — a deterministic parse result, so the URL will never
      // resolve no matter how many times it is retried. Routing 400 to
      // `unavailable` would leave garbage identifiers serving a 200 shell
      // forever: still a soft-404, just a noindexed one.
      //
      // Everything else is a backend problem rather than a missing document,
      // including 401/403/429 — this route is unauthenticated, so those can only
      // come from an edge/WAF and are transient by nature.
      return response.status === 400 ||
        response.status === 404 ||
        response.status === 410
        ? { status: 'notFound' }
        : { status: 'unavailable' };
    }

    const result = (await response.json()) as {
      data?: { data?: DocumentPageInterface | null };
    } | null;
    const doc = result?.data?.data;

    if (!doc) {
      // Deliberately `unavailable`, not `notFound`. The backend answers 404 for
      // a document that does not exist, so a 200 carrying no payload means the
      // response envelope drifted from `{data:{data: doc}}` — a deploy-time
      // contract break affecting EVERY document at once. `notFound` would hard-
      // 404 the whole corpus on that; `unavailable` degrades to a noindexed 200
      // that recovers as soon as the contract is restored.
      return { status: 'unavailable' };
    }

    return { status: 'ok', doc };
  } catch (error) {
    // Network failure, abort/timeout, malformed JSON — all transient-or-unknown.
    // Never `notFound`: a fetch that never reached the backend proves nothing
    // about whether the document exists.
    console.error('Error fetching document metadata:', error);
    return { status: 'unavailable' };
  }
}

/**
 * The document page's ONE `/document` call per render (#11821, #11822).
 *
 * `generateMetadata`, the page body and `fetchCaseLawForSsr` all need this
 * payload. Before this they each fetched it, and because every call carried its
 * own `AbortSignal`, Next's per-render fetch memoisation opted out and the
 * calls reached the backend together — up to three identical requests in the
 * same second, whose concurrent inline `ensure_decision_pdf` runs collided on
 * their non-waiting row lock (55P03, 59% of Postgres errors). `React.cache` memoizes
 * by argument for the lifetime of one server request, and the signal is
 * created inside the memoised function, so it no longer defeats the dedupe.
 *
 * `no-store`, deliberately, and it must stay that way: the payload carries
 * `urls.*` — Supabase signed URLs valid for 60 min, already up to 45 min old
 * when the backend's own Redis cache hands them out. A Data Cache entry
 * (`revalidate: 3600`, served stale-while-revalidate) returned them hours
 * after expiry, so SSR fetched a dead URL (Storage `400 InvalidJWT`, 98.5% of
 * failing gateway requests) and silently fell back to client render. A
 * response that carries a signed URL must never be cached downstream of the
 * signer.
 *
 * What this does NOT fix: it guarantees an ORIGIN render gets `urls.*` with at
 * least ~15 min left. The rendered page HTML itself is still edge-cached
 * (`s-maxage=3600, stale-while-revalidate=86400` for `/:locale/document|dokument`
 * in next.config.mjs), so a CDN-served page can carry expired seeded `urls.*`
 * until `DocumentViewer`'s client refetch replaces them. That is why #11821's
 * success measure is the InvalidJWT count from the server-side (`node`) user
 * agent, not browser 400s.
 *
 * Outside a React server render (tests, route handlers) `cache` is a
 * pass-through, so each call fetches.
 */
export const loadDocumentForRender = cache(
  (identifier: string): Promise<DocumentPayloadOutcome> =>
    fetchDocumentPayload(identifier, { cache: 'no-store' })
);

/** Parse `decision_courts`, which the backend sends as a JSON string or array. */
function parseCourts(
  raw: DocumentPageInterface['decision_courts']
): string[] | undefined {
  if (!raw) return undefined;
  if (Array.isArray(raw)) return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return [raw];
  }
}

function documentTypeOf(
  doc: DocumentPageInterface
): DocumentMetadataInfo['documentType'] {
  if (doc.iri) return 'legislation';
  if (doc.nsud_id) return 'nsud';
  if (doc.decision_type || doc.case_file_number) return 'decision';
  return 'unknown';
}

/** `2012-05-30T00:00:00+00:00` → `30. 05. 2012` (the courts' own format). */
function formatSlovakDate(iso: string | null | undefined): string | null {
  const match = iso?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}. ${match[2]}. ${match[1]}` : null;
}

/**
 * A per-decision description built from fields every decision carries.
 *
 * Most decisions have neither `decision_name` nor a subject of proceedings, and
 * the bare 'Súdne rozhodnutie' fallback rendered as the meta description
 * "Súdne rozhodnutie: Súdne rozhodnutie" on hundreds of thousands of pages —
 * one of the thin-content signals behind Search Console's 158k "Soft 404"
 * decision pages (export of 2026-10-28).
 */
function decisionSummary(doc: DocumentPageInterface): string | null {
  const parts = [
    doc.decision_decision_form,
    parseCourts(doc.decision_courts)?.[0],
    doc.case_file_number ? `sp. zn. ${doc.case_file_number}` : null,
    formatSlovakDate(doc.decision_release_date),
  ].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(', ') : null;
}

function titleAndDescription(
  doc: DocumentPageInterface,
  documentType: DocumentMetadataInfo['documentType']
): { documentTitle: string; description: string } {
  const fallbackTitle = doc.document_title || doc.file_name || 'Dokument';

  switch (documentType) {
    case 'legislation': {
      // For legislation: construct title as regulation_number - legislation_name
      const number = doc.legislation_regulation_number;
      const name = doc.legislation_name;
      const documentTitle =
        number && name
          ? `${number} - ${name}`
          : number || name || fallbackTitle;
      return {
        documentTitle,
        description: name || `Právny predpis ${number || ''}`,
      };
    }
    case 'nsud':
      return {
        documentTitle: fallbackTitle,
        description:
          doc.nsud_api_merito ||
          doc.nsud_merit ||
          'Rozhodnutie Najvyššieho súdu SR',
      };
    case 'decision':
      return {
        documentTitle: fallbackTitle,
        description:
          doc.decision_name ||
          doc.decision_subject_of_proceedings ||
          decisionSummary(doc) ||
          'Súdne rozhodnutie',
      };
    default:
      return { documentTitle: fallbackTitle, description: 'Právny dokument' };
  }
}

/** NSUD fields, preferring our columns over the raw NSUD API ones. */
function nsudFields(
  doc: DocumentPageInterface
): Pick<DocumentMetadataInfo, 'nsudCaseNumber' | 'nsudCollegium' | 'nsudDate'> {
  return {
    nsudCaseNumber: doc.nsud_case_number || doc.nsud_api_cislo || undefined,
    nsudCollegium: doc.nsud_collegium || doc.nsud_api_kolegium || undefined,
    nsudDate: doc.nsud_date || doc.nsud_api_datum || undefined,
  };
}

/** Project the raw payload onto the SEO shape. Drops `urls` by design. */
function toMetadataOutcome(
  payload: DocumentPayloadOutcome
): DocumentMetadataOutcome {
  if (payload.status !== 'ok') return payload;

  const { doc } = payload;
  const documentType = documentTypeOf(doc);
  const { documentTitle, description } = titleAndDescription(doc, documentType);

  const data: DocumentMetadataInfo = {
    documentTitle,
    description,
    documentType,
    // Legislation fields
    regulationNumber: doc.legislation_regulation_number ?? undefined,
    legislationName: doc.legislation_name ?? undefined,
    legislationYear: doc.legislation_year ?? undefined,
    iri: doc.iri ?? undefined,
    // Decision fields
    caseFileNumber: doc.case_file_number ?? undefined,
    decisionForm: doc.decision_decision_form ?? undefined,
    decisionType: doc.decision_type ?? undefined,
    courts: parseCourts(doc.decision_courts),
    ...nsudFields(doc),
    // Common
    originalSource: doc.original_source ?? undefined,
  };

  return { status: 'ok', data };
}

/**
 * Fetch document metadata, reporting *why* it failed.
 *
 * Prefer this over {@link fetchDocumentMetadata} anywhere the failure reason
 * changes the response (HTTP status, robots directives). `fetchDocumentMetadata`
 * remains the right call for purely presentational consumers (OG images), which
 * degrade identically whichever way the fetch failed.
 *
 * Reads through {@link loadDocumentForRender}, so inside one page render it
 * shares a single backend call with every other consumer of the payload.
 */
export async function documentMetadataOutcome(
  identifier: string
): Promise<DocumentMetadataOutcome> {
  return toMetadataOutcome(await loadDocumentForRender(identifier));
}

/**
 * Fetch document metadata from the unified document API.
 * Works with both numeric IDs and IRI paths.
 *
 * Returns `null` for both "missing" and "backend unavailable" — use
 * {@link documentMetadataOutcome} when that difference matters.
 *
 * Cached for an hour, unlike the page's render call: this path serves the OG
 * image route, which only ever sees the SEO projection. `toMetadataOutcome`
 * drops `urls`, so no signed URL can outlive its expiry through this cache.
 */
export async function fetchDocumentMetadata(
  identifier: string
): Promise<DocumentMetadataInfo | null> {
  const outcome = toMetadataOutcome(
    await fetchDocumentPayload(identifier, { next: { revalidate: 3600 } })
  );
  return outcome.status === 'ok' ? outcome.data : null;
}

/**
 * Returns the locale-specific URL path segment for document pages.
 *
 * Mapping must stay in sync with frontend/src/i18n/routing.ts pathnames:
 *   sk → 'dokument'   (/sk/dokument/[...segments])
 *   en → 'document'   (/en/document/[...segments])
 *
 * Exported so that relative-URL builders (markdownUtils, MessageActions)
 * share the single source of truth instead of duplicating the mapping.
 */
export function documentSlug(locale: string): 'dokument' | 'document' {
  return locale === 'sk' ? 'dokument' : 'document';
}

/**
 * Build the relative document page path for the given locale and identifier.
 *
 * This is the **generation** source of truth for internal document links.
 * All relative-URL builders should call this instead of inlining the
 * `locale === 'sk' ? 'dokument' : 'document'` ternary.
 *
 * @param locale - The current locale ('sk' or 'en')
 * @param identifier - Numeric document ID (string) or IRI path (e.g. 'SK/ZZ/1964/40')
 * @returns Relative path, e.g. '/sk/dokument/12345' or '/en/document/SK/ZZ/1964/40'
 */
export function buildDocumentPath(locale: string, identifier: string): string {
  return `/${locale}/${documentSlug(locale)}/${identifier}`;
}

/**
 * Extract the effective-date segment from a resolved (dated) legislation
 * IRI, e.g. '/SK/ZZ/1963/99/20250301' -> '20250301'. Returns null for an
 * undated IRI (e.g. '/SK/ZZ/1963/99') or a malformed one.
 *
 * Mirrors the backend's `parse_legislation_iri` (server/api/legislation.py):
 * a fully-resolved IRI has exactly 5 segments, and the 5th (index 4 after
 * dropping the leading empty segment from a leading slash) is an 8-digit
 * YYYYMMDD date. Used to propagate "the version being viewed" forward when
 * resolving a cross-reference link inside that version's body (issue #327
 * gap 1) — without this, every cross-reference always jumped to the
 * referenced law's LATEST version regardless of which version of the
 * citing law was open.
 */
export function extractEffectiveDateFromIri(iri: string): string | null {
  const segments = iri.split('/').filter(Boolean);
  const dateSegment = segments[4];
  return dateSegment && /^\d{8}$/.test(dateSegment) ? dateSegment : null;
}

/**
 * Render a compact `YYYYMMDD` date segment (the `?asOf=` value carried by a
 * cross-reference link, see extractEffectiveDateFromIri) as the Slovak
 * `D. M. YYYY` form used in user-facing copy, e.g. '19750701' -> '1. 7. 1975'.
 * Returns null for anything that is not exactly eight digits, so a
 * hand-edited URL never leaks a garbled date into an error message.
 */
export function formatAsOfDateSegment(dateSegment: string): string | null {
  if (!/^\d{8}$/.test(dateSegment)) return null;
  const year = dateSegment.slice(0, 4);
  const month = Number(dateSegment.slice(4, 6));
  const day = Number(dateSegment.slice(6, 8));
  return `${day}. ${month}. ${year}`;
}

/**
 * Slug-agnostic regex source string that matches both localized document slugs.
 *
 * Used by parse utilities (citationMatcher, citationExtractor) to extract
 * document identifiers from paths regardless of which locale slug was used
 * to generate the URL. Using this constant ensures all parsers stay in sync
 * with any future slug additions — a one-line change here propagates everywhere.
 *
 * Matches: /document/ and /dokument/
 *
 * @example
 *   new RegExp(`/(?:sk|en)/${DOCUMENT_SLUG_PATTERN_SRC}/(\\d+)`)
 */
export const DOCUMENT_SLUG_PATTERN_SRC = '(?:document|dokument)';

/**
 * Returns the canonical URL for a document page, using the locale-specific
 * slug so that og:url and JSON-LD @id agree on the same canonical path.
 *
 * Locale slug mapping must match frontend/src/i18n/routing.ts pathnames:
 *   sk → /dokument/[...segments]
 *   en → /document/[...segments]
 */
export function documentCanonicalUrl(
  locale: string,
  identifier: string
): string {
  return `${SITE_URL}${buildDocumentPath(locale, identifier)}`;
}

/**
 * Canonical + alternates for a document page, in either locale.
 *
 * Documents are Slovak legal texts: `/en/document/<id>` serves the same Slovak
 * body as `/sk/dokument/<id>` under English UI chrome, so it is a duplicate,
 * not a translation. Advertising it as `hreflang="en"` doubled the corpus for
 * Google (≈4.8M sitemap URLs; 530 of the 1,000 "Soft 404" examples in the
 * 2026-10-28 Search Console export were `/en/document/` URLs). Both locales
 * therefore declare the SK URL as canonical and no language alternates; the EN
 * page stays reachable for English-UI users.
 */
export function documentAlternates(identifier: string): { canonical: string } {
  return { canonical: documentCanonicalUrl('sk', identifier) };
}

/**
 * Get document type label in Slovak
 */
export function getDocumentTypeLabel(
  documentType: DocumentMetadataInfo['documentType']
): string {
  switch (documentType) {
    case 'legislation':
      return 'Zákon';
    case 'nsud':
      return 'Rozhodnutie NSUD';
    case 'decision':
      return 'Súdne rozhodnutie';
    default:
      return 'Dokument';
  }
}
